import { AccessibilityCategory } from "common/types";
import AccessibilitySubItem from "./AccessibilitySubItem";
import { bucketUrlPrefix } from "common/config";
import { useDarkMode } from "@/hooks/useDarkMode";

type AccessibilityItemProps = {
  category: AccessibilityCategory;
  fullSize?: boolean;
  venueId: string;
  // Bubbles the flag request up to AccessibilitySection, which owns the single
  // FlagVenueAccessibility modal (prevents one modal per category from stacking).
  onFlag: (mainTag: any) => void;
  venueDetails: any;
  categories: AccessibilityCategory[];
  lastUpdated: string;
  cid?: string;
  tid?: string;
  onShowDetailView?: (initialSpaceId: string) => void;
};

function AccessibilityItem({
  category,
  fullSize,
  venueId,
  onFlag,
  venueDetails,
  categories,
  lastUpdated,
  cid,
  tid,
  onShowDetailView,
}: AccessibilityItemProps) {
  const isDark = useDarkMode();

  // Generate a unique ID for the heading
  const headingId = `category-heading-${category.catId}`;
  // categories.name is nullable; the count beside it is hidden below md, so a blank
  // name would leave the heading with no accessible text at all.
  const catName = category.catName?.trim();

  return (
    <section aria-labelledby={catName ? headingId : undefined}>
      {/* Category Heading */}
      {catName && (
        <h2 id={headingId} className="label-l-bold text-fg-neutral-strong py-4">
          <span className="flex items-center gap-2 w-full">
            {category.catImage && (
              <img
                src={
                  bucketUrlPrefix +
                  (isDark && category.catImageDark
                    ? category.catImageDark
                    : category.catImage)
                }
                alt=""
                aria-hidden="true"
                className={`w-6 h-6 flex-shrink-0 ${isDark && !category.catImageDark ? "filter brightness-0 invert" : ""}`}
              />
            )}

            <span className="title-s-semibold text-fg-neutral-strong">
              {catName}
            </span>

            <span className="hidden md:inline title-s-semibold text-fg-neutral-strong">({category.tags.length})</span>
          </span>
        </h2>
      )}

      {/* Space Cards */}
      <div className="flex flex-col gap-4">
        {category.tags.map((tag, index) => {
          let reviewPhotos = tag?.reviewPhotos;
          if (category.catId === 96) {
            // Indoors VSH - Find Indoors
            const indoor = categories.find((c) => c.catId === 58);
            if (indoor) {
              if (indoor.tags[index]) {
                reviewPhotos = reviewPhotos.concat(
                  indoor.tags[index]?.reviewPhotos,
                );
              }
            }
          } else if (category.catId === 216) {
            // Outdoors VSH - Find Outdoors
            const indoor = categories.find((c) => c.catId === 172);
            if (indoor) {
              if (indoor.tags[index]?.reviewPhotos) {
                reviewPhotos = reviewPhotos.concat(
                  indoor.tags[index]?.reviewPhotos,
                );
              }
            }
          }
          return (
            <AccessibilitySubItem
              key={index}
              venueId={venueId}
              latestReviewDetailUpdatedDate={tag.latestReviewDetailUpdatedDate}
              qtags={tag.qtags}
              qtagsAll={tag.qtagsAll}
              sections={category.cat_display_sections}
              reviewPhotos={reviewPhotos}
              fullSize={fullSize}
              venueDetails={venueDetails}
              categories={categories}
              categoryName={category.catName}
              onFlag={(mainTag) => onFlag(mainTag)}
              lastUpdated={lastUpdated}
              cid={cid}
              tid={tid}
              onShowDetailView={onShowDetailView}
            />
          );
        })}
      </div>
    </section>
  );
}

export default AccessibilityItem;
